import React, { useState, useEffect } from 'react';
import { Loader2, Plus, Trash2, Edit2, Save, X, Trophy, ArrowUpDown } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const SuccessStoriesPanel = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const { getAuthHeaders } = useAuth();

  const emptyStory = {
    title: '',
    client: '',
    industry: '',
    image: '',
    stat: '',
    stat_label: '',
    description: '',
    order: 0,
    is_active: true
  };

  const [formData, setFormData] = useState(emptyStory);

  useEffect(() => {
    loadStories();
  }, []);

  const loadStories = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cms/success-stories?active_only=false`);
      const data = await response.json();
      setStories(data);
    } catch (error) {
      toast.error('Failed to load success stories');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const url = editingId 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/cms/success-stories/${editingId}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/cms/success-stories`;
      
      const response = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ...formData,
          order: parseInt(formData.order) || 0
        })
      });

      if (!response.ok) throw new Error('Failed to save');

      toast.success(editingId ? 'Story updated!' : 'Story created!');
      setFormData(emptyStory);
      setEditingId(null);
      setShowAddForm(false);
      loadStories();
    } catch (error) {
      toast.error('Failed to save success story');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (story) => {
    setFormData({
      title: story.title,
      client: story.client,
      industry: story.industry,
      image: story.image,
      stat: story.stat,
      stat_label: story.stat_label,
      description: story.description || '',
      order: story.order,
      is_active: story.is_active
    });
    setEditingId(story.id);
    setShowAddForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this success story?')) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cms/success-stories/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!response.ok) throw new Error('Failed to delete');

      toast.success('Story deleted!');
      loadStories();
    } catch (error) {
      toast.error('Failed to delete success story');
    }
  };

  const handleCancel = () => {
    setFormData(emptyStory);
    setEditingId(null);
    setShowAddForm(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-[#320547]" size={48} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Trophy size={24} className="text-[#95228A]" />
            Success Stories
          </CardTitle>
          {!showAddForm && (
            <Button onClick={() => setShowAddForm(true)} className="bg-[#320547] hover:bg-[#95228A]">
              <Plus size={18} className="mr-1" /> Add Story
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {/* Add/Edit Form */}
          {showAddForm && (
            <form onSubmit={handleSubmit} className="mb-8 p-6 bg-gray-50 rounded-xl space-y-4">
              <h3 className="font-semibold text-lg mb-4">
                {editingId ? 'Edit Success Story' : 'Add New Success Story'}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Client Name *</label>
                  <Input
                    name="client"
                    value={formData.client}
                    onChange={handleChange}
                    placeholder="e.g., MindGuard AI"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Project Title *</label>
                  <Input
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="e.g., AI Suicide Prevention App"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Industry *</label>
                  <Input
                    name="industry"
                    value={formData.industry}
                    onChange={handleChange}
                    placeholder="e.g., Healthcare, EdTech"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Key Stat *</label>
                  <Input
                    name="stat"
                    value={formData.stat}
                    onChange={handleChange}
                    placeholder="e.g., 45%, 10K+, 99.9%"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Stat Label *</label>
                  <Input
                    name="stat_label"
                    value={formData.stat_label}
                    onChange={handleChange}
                    placeholder="e.g., Faster Dispatch"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Image URL *</label>
                <Input
                  name="image"
                  value={formData.image}
                  onChange={handleChange}
                  placeholder="https://images.unsplash.com/..."
                  required
                />
                {formData.image && (
                  <img src={formData.image} alt="Preview" className="mt-2 h-24 w-40 object-cover rounded-lg" />
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Description (Optional)</label>
                <Textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Brief description of the project and results..."
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Display Order</label>
                  <Input
                    type="number"
                    name="order"
                    value={formData.order}
                    onChange={handleChange}
                    min="0"
                  />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleChange}
                    className="w-4 h-4"
                  />
                  <label className="text-sm font-medium">Active (visible on website)</label>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="submit" disabled={saving} className="bg-[#320547] hover:bg-[#95228A]">
                  {saving ? <Loader2 className="animate-spin mr-2" size={18} /> : <Save size={18} className="mr-2" />}
                  {editingId ? 'Update Story' : 'Create Story'}
                </Button>
                <Button type="button" variant="outline" onClick={handleCancel}>
                  <X size={18} className="mr-2" /> Cancel
                </Button>
              </div>
            </form>
          )}

          {/* Stories List */}
          {stories.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Trophy size={48} className="mx-auto mb-4 opacity-30" />
              <p>No success stories yet. Add your first one!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {stories.map((story) => (
                <div 
                  key={story.id} 
                  className={`relative rounded-xl overflow-hidden border ${story.is_active ? 'border-gray-200' : 'border-red-200 bg-red-50/50'}`}
                >
                  <div className="relative h-32">
                    <img src={story.image} alt={story.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                    <div className="absolute bottom-2 left-2 right-2">
                      <span className="text-white font-bold text-lg">{story.stat}</span>
                      <span className="text-white/80 text-xs block">{story.stat_label}</span>
                    </div>
                    <span className="absolute top-2 left-2 px-2 py-0.5 bg-[#95228A] rounded text-white text-xs">
                      {story.industry}
                    </span>
                    {!story.is_active && (
                      <span className="absolute top-2 right-2 px-2 py-0.5 bg-red-500 rounded text-white text-xs">
                        Inactive
                      </span>
                    )}
                  </div>
                  <div className="p-4">
                    <h4 className="font-bold text-gray-900">{story.client}</h4>
                    <p className="text-gray-500 text-sm">{story.title}</p>
                    <div className="flex items-center gap-1 mt-1 text-xs text-gray-400">
                      <ArrowUpDown size={12} /> Order: {story.order}
                    </div>
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" variant="outline" onClick={() => handleEdit(story)}>
                        <Edit2 size={14} className="mr-1" /> Edit
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(story.id)}>
                        <Trash2 size={14} className="mr-1" /> Delete
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SuccessStoriesPanel;
